<template>
  <div class="device-config-page" v-if="deviceOnline()">
    
    <b-spinner v-if="loading && !getConfigFailure" class="config-page-loading-spinner"></b-spinner>
    <div v-if="getConfigFailure" class="config-loading-error">Error loading configuration - please try again later</div>
    <div v-if="!getConfigFailure" v-bind:class="{ submitted: loading }">
    <b-form @submit.prevent="saveConfig" novalidate>
      <div class="card panel mb-xs">
        <div
          class="card-header"
          role="button"
          @click="tabs.hfExpanded = !tabs.hfExpanded"
        >
          <div class="mb-0">
            <a class="accordion-toggle" role="button">
              HF Channels
              <i
                class="fas"
                :class="[(tabs.hfExpanded) ? 'fa-angle-up':'fa-angle-down']"
              />
            </a>
          </div>
        </div>
        <b-collapse
          id="hf-channel-content"
          class="panel-body"
          v-model="tabs.hfExpanded"
          v-if="localConfig && localConfig.reconfigured"
        >
          <div class="card-body">
            <b-form-row>
              <b-col>
                <TableWithInputs
                  :tableHeadings="mlsTable.headings"
                  :tableData="mlsTable.rows"
                  :model="localConfig.reconfigured"
                  :inputDisabled="!$base.isAllowed('editDeviceConfiguration')"
                />
              </b-col>
            </b-form-row>
          </div>
        </b-collapse>
      </div>
      <div class="card panel mb-xs">
        <div
          class="card-header"
          role="button"
          @click="tabs.lfExpanded = !tabs.lfExpanded"
        >
          <div class="mb-0">
            <a class="accordion-toggle" role="button">
              LF Channels
              <i
                class="fas"
                :class="[(tabs.lfExpanded) ? 'fa-angle-up':'fa-angle-down']"
              />
            </a>
          </div>
        </div>
        <b-collapse id="lf-channel-content" class="panel-body" v-model="tabs.lfExpanded">
          <div class="card-body">
            <b-form-row v-if="localConfig && (localConfig.trigger_mode >= 0)">
              <b-col>
                <FormSection title="General">
                  <b-form-group label="Trigger Mode" label-for="trigger-mode">
                    <b-form-select
                      id="trigger-mode"
                      :required="true"
                      :options="triggerModes"
                      class="w-25"
                      v-model="localConfig.trigger_mode"
                      :disabled="!$base.isAllowed('editDeviceConfiguration')"
                    ></b-form-select>
                  </b-form-group>
                </FormSection>
              </b-col>
            </b-form-row>
            <b-form-row v-if="localConfig && localConfig.nsm">
              <b-col>
                <FormSection title="Network State Machine Settings">
                  <b-form-row>
                    <b-col cols="3">
                      <b-form-group
                        id="phaseStatusOnToleranceGroup"
                        label="Phase Status On Tolerance"
                        label-for="phaseStatusOnTolerance"
                      >
                        <b-input
                          id="phaseStatusOnTolerance"
                          type="number"
                          required
                          placeholder="Phase Status On Tolerance"
                          v-model.number="localConfig.nsm.phase_status_on_tolerance"
                          :disabled="!$base.isAllowed('editDeviceConfiguration')"
                        ></b-input>
                      </b-form-group>
                    </b-col>
                    <b-col cols="3">
                      <b-form-group
                        id="phaseStatusOffToleranceGroup"
                        label="Phase Status Off Tolerance"
                        label-for="phaseStatusOffTolerance"
                      >
                        <b-input
                          id="phaseStatusOffTolerance"
                          type="number"
                          required
                          placeholder="Phase Status Off Tolerance"
                          v-model.number="localConfig.nsm.phase_status_off_tolerance"
                          :disabled="!$base.isAllowed('editDeviceConfiguration')"
                        ></b-input>
                      </b-form-group>
                    </b-col>
                  </b-form-row>
                </FormSection>
              </b-col>
            </b-form-row>
            <b-form-row v-if="localConfig && localConfig.reconfigured">
              <b-col>
                <FormSection title="Trigger Activation">
                  <TableWithInputs
                    cssClasses="w-50"
                    :tableHeadings="triggerActivationTable.headings"
                    :tableData="triggerActivationTable.rows"
                    :model="localConfig.reconfigured"
                    :inputDisabled="!$base.isAllowed('editDeviceConfiguration')"
                  />
                </FormSection>
              </b-col>
            </b-form-row>
            <b-form-row>
              <b-col>
                <FormSection title="Trigger Thresholds" v-if="($base.isAllowed('editDeviceConfiguration')) || (localConfig && localConfig.reconfigured &&localConfig.reconfigured.trigger_config_enabled)">
                  <b-row>
                    <b-col v-if="localConfig && localConfig.reconfigured">
                      <IOSSwitch
                        id="enable-custom-trigger-thresholds"
                        :checked="localConfig.reconfigured.trigger_config_enabled"
                        v-model="localConfig.reconfigured.trigger_config_enabled"
                        label="Custom Thresholds"
                        :inputDisabled="!$base.isAllowed('editDeviceConfiguration')"
                      />
                    </b-col>
                  </b-row>
                  <div
                    v-if="localConfig && localConfig.reconfigured && localConfig.reconfigured.trigger_config_enabled"
                  >
                    <b-row>
                      <b-col>
                        <TableWithInputs
                          :tableHeadings="zeroSequenceTriggerTable.headings"
                          :tableData="zeroSequenceTriggerTable.rows"
                          :model="localConfig.reconfigured"
                          :inputDisabled="!$base.isAllowed('editDeviceConfiguration')"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <TableWithInputs
                          :tableHeadings="underVoltageTriggerTable.headings"
                          :tableData="underVoltageTriggerTable.rows"
                          :model="localConfig.trigger_config.under_voltage_trigger"
                          :inputDisabled="!$base.isAllowed('editDeviceConfiguration')"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <TableWithInputs
                          :tableHeadings="overCurrentTriggerTable.headings"
                          :tableData="overCurrentTriggerTable.rows"
                          :model="localConfig.trigger_config.over_current_trigger"
                          :inputDisabled="!$base.isAllowed('editDeviceConfiguration')"
                        />
                      </b-col>
                    </b-row>
                  </div>
                </FormSection>
              </b-col>
            </b-form-row>
          </div>
        </b-collapse>
      </div>
      <b-form-row class="float-right">
        <b-button type="submit" variant="primary" v-if="!loading && $base.isAllowed('editDeviceConfiguration')">Save</b-button>
      </b-form-row>
    </b-form>
    </div>
  </div>
</template>

<script>
import IOSSwitch from "@/components/IOSSwitch/IOSSwitch";
import TableWithInputs from "@/components/Forms/TableWithInputs";
import FormSection from "@/components/Forms/FormSection";
import { mapState, mapActions, mapGetters } from "vuex";
import { cloneDeep } from "lodash";

export default {
  name: "DeviceConfigTab",
  components: {
    IOSSwitch,
    TableWithInputs,
    FormSection
  },
  props: {
    getConfigFailure: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapState("device", {
      config: state => state.config
    })
  },
  methods: {
    ...mapActions("device", ["getDeviceConfig", "saveDeviceConfig"]),
    ...mapGetters('device', ['deviceOnline']),
    saveConfig: function() {
      this.loading = true;
      this.saveDeviceConfig({ config: this.localConfig, serial: this.$route.params.serial }).catch(()=>{
        // Error submitting - allow the user to make changes again
        this.loading = false;
      });
    }
  },
  watch: {
    config(newValue, oldValue) {
      if(newValue.reconfigured && (newValue!=oldValue)){
        this.localConfig = cloneDeep(newValue);
        this.loading = false;
      }
    }
  },
  data() {
    return {
      localConfig: {},
      loading: false,
      tabs: {
        hfExpanded: true,
        lfExpanded: true
      },
      mlsTable: {
        headings: [
          {
            name: "Phase",
            cssClass: "w-25"
          },
          {
            name: "MLS Sequence",
            cssClass: "w-10"
          },
          {
            name: "MLS A",
            cssClass: "w-10"
          },
          {
            name: "MLS B",
            cssClass: "w-10"
          },
          {
            name: "MLS C",
            cssClass: "w-10"
          },
          {
            name: "Analog Gain TX",
            cssClass: "w-10"
          },
          {
            name: "Analog Gain RX",
            cssClass: "w-10"
          },
          {
            name: "Current Polarity - Inverted",
            cssClass: "w-15"
          }
        ],
        rows: [
          [
            {
              type: "info",
              value: "Phase 1"
            },
            {
              type: "switch",
              inputProps: {
                id: "phase-1-mls-sequence",
                required: true,
                modelPropName: "phase1MLSSequence"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-1-mls-a",
                type: "number",
                required: true,
                modelPropName: "phase1MLSA",
                placeholder: "A",
                name: "phase1MLSA"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-1-mls-b",
                type: "number",
                required: true,
                modelPropName: "phase1MLSB",
                placeholder: "B",
                name: "phase1MLSB"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-1-mls-c",
                type: "number",
                required: true,
                modelPropName: "phase1MLSC",
                placeholder: "C",
                name: "phase1MLSC"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-1-analog-gain-tx",
                type: "number",
                required: true,
                modelPropName: "phase1AnalogGainTX",
                placeholder: "TX",
                name: "phase1AnalogGainTX"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-1-analog-gain-rx",
                type: "number",
                required: true,
                modelPropName: "phase1AnalogGainRX",
                placeholder: "RX",
                name: "phase1AnalogGainRX"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "phase-1-current-polarity",
                required: true,
                modelPropName: "phase1CurrentPolarityInverted"
              }
            }
          ],
          [
            {
              type: "info",
              value: "Phase 2"
            },
            {
              type: "switch",
              inputProps: {
                id: "phase-2-mls-sequence",
                required: true,
                modelPropName: "phase2MLSSequence"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-2-mls-a",
                type: "number",
                required: true,
                modelPropName: "phase2MLSA",
                placeholder: "A",
                name: "phase2MLSA"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-2-mls-b",
                type: "number",
                required: true,
                modelPropName: "phase2MLSB",
                placeholder: "B",
                name: "phase2MLSB"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-2-mls-c",
                type: "number",
                required: true,
                modelPropName: "phase2MLSC",
                placeholder: "C",
                name: "phase2MLSC"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-2-analog-gain-tx",
                type: "number",
                required: true,
                modelPropName: "phase2AnalogGainTX",
                placeholder: "TX",
                name: "phase2AnalogGainTX"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-2-analog-gain-rx",
                type: "number",
                required: true,
                modelPropName: "phase2AnalogGainRX",
                placeholder: "RX",
                name: "phase2AnalogGainRX"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "phase-2-current-polarity",
                required: true,
                modelPropName: "phase2CurrentPolarityInverted"
              }
            }
          ],
          [
            {
              type: "info",
              value: "Phase 3"
            },
            {
              type: "switch",
              inputProps: {
                id: "phase-3-mls-sequence",
                required: true,
                modelPropName: "phase3MLSSequence"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-3-mls-a",
                type: "number",
                required: true,
                modelPropName: "phase3MLSA",
                placeholder: "A",
                name: "phase3MLSA"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-3-mls-b",
                type: "number",
                required: true,
                modelPropName: "phase3MLSB",
                placeholder: "B",
                name: "phase3MLSB"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-3-mls-c",
                type: "number",
                required: true,
                modelPropName: "phase3MLSC",
                placeholder: "C",
                name: "phase3MLSC"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-3-analog-gain-tx",
                type: "number",
                required: true,
                modelPropName: "phase3AnalogGainTX",
                placeholder: "TX",
                name: "phase3AnalogGainTX"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "phase-3-analog-gain-rx",
                type: "number",
                required: true,
                modelPropName: "phase3AnalogGainRX",
                placeholder: "RX",
                name: "phase3AnalogGainRX"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "phase-3-current-polarity",
                required: true,
                modelPropName: "phase3CurrentPolarityInverted"
              }
            }
          ]
        ]
      },
      triggerActivationTable: {
        headings: [
          {
            name: "",
            cssClass: "w-20"
          },
          {
            name: "Channel 1"
          },
          {
            name: "Channel 2"
          },
          {
            name: "Channel 3"
          }
        ],
        rows: [
          [
            {
              type: "info",
              value: "Under Voltage"
            },
            {
              type: "switch",
              inputProps: {
                id: "trigger-activation-under-voltage-channel-1",
                required: true,
                modelPropName: "underVoltageChannel1"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "trigger-activation-under-voltage-channel-2",
                required: true,
                modelPropName: "underVoltageChannel2"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "trigger-activation-under-voltage-channel-3",
                required: true,
                modelPropName: "underVoltageChannel3"
              }
            }
          ],
          [
            {
              type: "info",
              value: "Over Current"
            },
            {
              type: "switch",
              inputProps: {
                id: "trigger-activation-over-voltage-channel-1",
                required: true,
                modelPropName: "overVoltageChannel1"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "trigger-activation-over-voltage-channel-2",
                required: true,
                modelPropName: "overVoltageChannel2"
              }
            },
            {
              type: "switch",
              inputProps: {
                id: "trigger-activation-over-voltage-channel-3",
                required: true,
                modelPropName: "overVoltageChannel3"
              }
            }
          ]
        ]
      },
      zeroSequenceTriggerTable: {
        headings: [
          {
            name: "Zero Sequence Trigger",
            cssClass: "w-20"
          },
          {
            name: "Amplitude Threshold",
            cssClass: "w-20"
          },
          {
            name: "Amplitude Hysteresis",
            cssClass: "w-20"
          },
          {
            name: "VEC Shift Threshold",
            cssClass: "w-20"
          },
          {
            name: "VEC Shift Hysteresis",
            cssClass: "w-20"
          }
        ],
        rows: [
          [
            {
              type: "info",
              value: "Current"
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-current-amp-threshold",
                type: "number",
                required: true,
                modelPropName: "currentAmpThreshold",
                placeholder: "Amplitude Threshold",
                name: "currentAmpThreshold"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-current-amp-hysteresis",
                type: "number",
                required: true,
                modelPropName: "currentAmpHysteresis",
                placeholder: "Amplitude Hysteresis",
                name: "currentAmpHysteresis"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-current-vec-threshold",
                type: "number",
                required: true,
                modelPropName: "currentVecThreshold",
                placeholder: "VEC Shift Threshold",
                name: "currentVecThreshold"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-current-vec-hysteresis",
                type: "number",
                required: true,
                modelPropName: "currentVecHysteresis",
                placeholder: "VEC Shift Hysteresis",
                name: "currentVecHysteresis"
              }
            }
          ],
          [
            {
              type: "info",
              value: "Voltage"
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-voltage-amp-threshold",
                type: "number",
                required: true,
                modelPropName: "voltageAmpThreshold",
                placeholder: "Amplitude Threshold",
                name: "voltageAmpThreshold"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-voltage-amp-hysteresis",
                type: "number",
                required: true,
                modelPropName: "voltageAmpHysteresis",
                placeholder: "Amplitude Hysteresis",
                name: "voltageAmpHysteresis"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-voltage-vec-threshold",
                type: "number",
                required: true,
                modelPropName: "voltageVecThreshold",
                placeholder: "VEC Shift Threshold",
                name: "voltageVecThreshold"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "zero-sequence-trigger-voltage-vec-hysteresis",
                type: "number",
                required: true,
                modelPropName: "voltageVecHysteresis",
                placeholder: "VEC Shift Hysteresis",
                name: "voltageVecHysteresis"
              }
            }
          ]
        ]
      },
      underVoltageTriggerTable: {
        headings: [
          {
            name: "Trigger",
            cssClass: "w-20"
          },
          {
            name: "Voltage Clip - Lower",
            cssClass: "w-20"
          },
          {
            name: "Voltage Clip - Upper",
            cssClass: "w-20"
          },
          {
            name: "Level",
            cssClass: "w-20"
          },
          {
            name: "Hysteresis Level",
            cssClass: "w-20"
          }
        ],
        rows: [
          [
            {
              type: "info",
              value: "Under Voltage"
            },
            {
              type: "input",
              inputProps: {
                id: "under-voltage-trigger-clip-lower",
                type: "number",
                required: true,
                modelPropName: "clip_lower",
                placeholder: "Voltage Clip - Lower",
                name: "clipLower"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "under-voltage-trigger-clip-upper",
                type: "number",
                required: true,
                modelPropName: "clip_upper",
                placeholder: "Voltage Clip - Upper",
                name: "clipUpper"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "under-voltage-trigger-level",
                type: "number",
                required: true,
                modelPropName: "threshold",
                placeholder: "Level",
                name: "level"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "under-voltage-trigger-hysteresis-level",
                type: "number",
                required: true,
                modelPropName: "hysteresis",
                placeholder: "Hysteresis Level",
                name: "hysteresisLevel"
              }
            }
          ]
        ]
      },
      overCurrentTriggerTable: {
        headings: [
          {
            name: "Trigger",
            cssClass: "w-20"
          },
          {
            name: "Current Clip - Upper"
          },
          {
            name: "Level"
          },
          {
            name: "Hysteresis Level"
          }
        ],
        rows: [
          [
            {
              type: "info",
              value: "Over Current"
            },
            {
              type: "input",
              inputProps: {
                id: "over-current-trigger-clip-upper",
                type: "number",
                required: true,
                modelPropName: "clip_upper",
                placeholder: "Current Clip - Upper",
                name: "clipUpper"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "over-current-trigger-level",
                type: "number",
                required: true,
                modelPropName: "threshold",
                placeholder: "Level",
                name: "level"
              }
            },
            {
              type: "input",
              inputProps: {
                id: "over-current-trigger-hysteresis-level",
                type: "number",
                required: true,
                modelPropName: "hysteresis",
                placeholder: "Hysteresis Level",
                name: "hysteresisLevel"
              }
            }
          ]
        ]
      },
      triggerModes: [
        {
          text: "Manual",
          value: 0
        },
        {
          text: "Event Driven",
          value: 2
        },
        {
          text: "Timed Sequence",
          value: 3
        }
      ],
      zeroSequenceTriggers: [
        {
          name: "Current"
        },
        {
          name: "Voltage"
        }
      ]
    };
  },  // data()
  mounted() {
    if (this.deviceOnline()) {
      this.loading = true;
      this.getDeviceConfig(this.$route.params.serial).then().catch(e => {
        this.getConfigFailure = true
      });

    }
  }
};
</script>

<style src="./ConfigTab.scss" lang="scss" scoped/>
